import type { IssuedCredentials } from '../credentials/issued-credentials'
import type { ImportIssue, ValidationResult } from '~/utils/importCsv/utils'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import { Download, Loader2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { SegmentedControl } from '~/components/SegmentedControl'
import { SidePanel } from '~/components/SidePanel'
import { Button } from '~/components/ui/button'
import { GQL_EXISTING_USER_EMAILS, GQL_IMPORT_USERS } from '~/gql/users/users'
import { client } from '~/helpers/apollo'
import { cn } from '~/lib/utils'
import { downloadCSV } from '~/utils/exportCsv/utils'
import {
  parseUsersCsv,
  USERS_CSV_HEADERS,
  validateUserRows,
} from '~/utils/importCsv/utils'
import ImportIssuesTable from './ImportIssuesTable'
import UsernameImport from './UsernameImport'

type Props = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void
  /** Apprenants en pseudo importés (#271) : leurs identifiants, à afficher une fois. */
  onIssued?: (credentials: IssuedCredentials[]) => void
}

type ReportError = { line: number, email: string, reason: string }
type Report = { createdCount: number, errorCount: number, errors: ReportError[] }

/**
 * Registre du bilan d'import : le vert plein ne se mérite qu'en l'absence de
 * ligne refusée. Un import intégralement en échec s'affichait auparavant en
 * vert au-dessus de son propre tableau d'erreurs (#131).
 */
type ReportTone = 'success' | 'partial' | 'failure'

function reportTone({ createdCount, errorCount }: Report): ReportTone {
  if (createdCount === 0) return 'failure'
  return errorCount > 0 ? 'partial' : 'success'
}

const TONE_CLASS: Record<ReportTone, string> = {
  success: 'text-success-text',
  partial: 'text-warning-text',
  failure: 'text-error-text',
}

/**
 * Libellé du bilan. « 0 compte(s) créé(s) » se lisant mal, le cas sans création
 * a sa propre formulation, centrée sur les lignes refusées (#131).
 */
function reportLabel(report: Report): { key: string, params: Record<string, number> } {
  switch (reportTone(report)) {
    case 'failure':
      return { key: 'import.report.none', params: { count: report.errorCount } }
    case 'partial':
      return { key: 'import.report.partial', params: { count: report.createdCount, failed: report.errorCount } }
    default:
      return { key: 'import.report.created', params: { count: report.createdCount } }
  }
}

/** Lignes refusées, sous la forme du tableau partagé avec l'import en pseudo. */
function IssuesTable({ issues }: { issues: (ImportIssue | ReportError)[] }) {
  const { t } = useTranslation()
  return <ImportIssuesTable valueLabel={t('common.email')} issues={issues.map(({ line, email, reason }) => ({ line, value: email, reason }))} />
}

const NOTHING_CHECKED: ValidationResult = { valid: [], issues: [] }

function UserImportModal({ open, onCancel, onSuccess, onIssued }: Props) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [checked, setChecked] = useState<ValidationResult>(NOTHING_CHECKED)
  const [checking, setChecking] = useState(false)
  const [report, setReport] = useState<Report | null>(null)
  const [mode, setMode] = useState<'email' | 'username'>('email')

  const [importUsers, { loading }] = useMutation(GQL_IMPORT_USERS, { client })
  const [fetchExistingEmails] = useLazyQuery(GQL_EXISTING_USER_EMAILS, { client })

  const reset = () => {
    setFileName(null)
    setChecked(NOTHING_CHECKED)
    setChecking(false)
    setReport(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const close = () => {
    reset()
    onCancel()
  }

  const downloadTemplate = () => {
    downloadCSV('modele-import-apprenants', [...USERS_CSV_HEADERS], [
      ['exemple@domaine.fr', 'Jean', 'Dupont', 'Mairie de Lyon', 'Acme', 'Ressources humaines'],
    ])
  }

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setReport(null)
    setFileName(file.name)

    const text = await file.text()
    const result = parseUsersCsv(text)
    if (!result.ok) {
      setChecked(NOTHING_CHECKED)
      toast.error(t(result.error === 'empty' ? 'import.error.empty' : 'import.error.badHeader'))
      return
    }

    // Contrôles locaux d'abord : ils s'affichent sans attendre le réseau, et
    // bornent la liste d'adresses envoyée à la pré-vérification.
    const local = validateUserRows(result.rows)
    setChecked(local)
    if (local.valid.length === 0) return

    setChecking(true)
    try {
      const { data } = await fetchExistingEmails({
        variables: { emails: local.valid.map(({ row }) => row.email) },
      })
      setChecked(validateUserRows(result.rows, data?.getExistingUserEmails ?? []))
    } catch {
      // La pré-vérification est un confort : son échec ne bloque pas l'import,
      // `importUsers` refusant de toute façon les adresses déjà prises.
      toast.warning(t('import.check.failed'))
    } finally {
      setChecking(false)
    }
  }

  const onImport = async () => {
    const sent = checked.valid
    if (sent.length === 0) return
    try {
      const { data } = await importUsers({ variables: { users: sent.map(({ row }) => row) } })
      const result = data?.importUsers as Report | undefined
      if (!result) throw new Error('no data')

      // Le serveur numérote les lignes du tableau qu'il a reçu : sans cette
      // remise en correspondance, ses erreurs désigneraient les mauvaises lignes
      // du fichier dès qu'une ligne a été écartée en amont. Les lignes écartées
      // rejoignent le bilan, sinon elles disparaîtraient du décompte.
      const errors: ReportError[] = [
        ...checked.issues,
        ...result.errors.map(err => ({ ...err, line: sent[err.line - 1]?.line ?? err.line })),
      ].sort((a, b) => a.line - b.line)
      setReport({ createdCount: result.createdCount, errorCount: errors.length, errors })

      // Une mutation qui répond « 0 créé » reste un échec pour l'admin : sans
      // cette branche, le seul retour était la ligne de bilan (#131).
      if (result.createdCount === 0) {
        toast.error(t('bad.import.user.none'))
        return
      }
      if (errors.length > 0) toast.warning(t('warn.import.user.partial', { count: errors.length }))
      else toast.success(t('good.import.user'))
      onSuccess()
    } catch {
      toast.error(t('bad.import.user'))
    }
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={close}
      size="lg"
      title={t('import.user')}
      footer={(
        <>
          <Button variant="outline" type="button" onClick={close}>
            {report ? t('common.close') : t('common.cancel')}
          </Button>
          {!report && mode === 'email' && (
            <Button
              type="button"
              disabled={checked.valid.length === 0 || checking || loading}
              onClick={onImport}
            >
              {checked.valid.length > 0
                ? t('import.submit', { count: checked.valid.length })
                : t('common.import')}
            </Button>
          )}
        </>
      )}
    >
      <SegmentedControl
        className="mb-4"
        label={t('user.mode.label')}
        options={[{ value: 'email', label: t('user.mode.email') }, { value: 'username', label: t('user.mode.username') }]}
        value={mode}
        onChange={setMode}
      />
      {mode === 'username' && (
        <UsernameImport
          onImported={(credentials) => {
            onSuccess()
            onIssued?.(credentials)
          }}
        />
      )}
      <div className="space-y-4" hidden={mode === 'username'}>
        <p className="text-sm text-muted-foreground">
          {t('import.csv.help', { headers: USERS_CSV_HEADERS.join(' ; ') })}
        </p>

        {/* Le groupe est créé côté serveur s'il n'existe pas : à dire, sinon
            l'admin croit devoir le créer d'abord. */}
        <p className="text-sm text-muted-foreground">
          {t('import.csv.groupHelp')}
        </p>

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={onFile}
          />
          <Button variant="outline" onClick={() => inputRef.current?.click()}>
            <Upload className="mr-2 size-4" />
            {t('click.to.upload')}
          </Button>
          <Button variant="ghost" onClick={downloadTemplate}>
            <Download className="mr-2 size-4" />
            {t('download.template')}
          </Button>
        </div>

        {/* Aperçu : ce que l'import va faire, avant de le faire (#132). */}
        {fileName && !report && (
          <div className="space-y-2">
            <p className="text-sm">
              {t('import.csv.parsed', {
                count: checked.valid.length + checked.issues.length,
                file: fileName,
              })}
            </p>
            <p className={cn(
              'text-sm font-medium',
              checked.valid.length === 0 ? TONE_CLASS.failure : TONE_CLASS.success,
            )}
            >
              {checked.valid.length === 0
                ? t('import.preview.none')
                : t('import.preview.valid', { count: checked.valid.length })}
            </p>
            {checking && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('import.preview.checking')}
              </p>
            )}
            {checked.issues.length > 0 && (
              <>
                <p className="text-sm text-muted-foreground">
                  {t('import.preview.skipped', { count: checked.issues.length })}
                </p>
                <IssuesTable issues={checked.issues} />
              </>
            )}
          </div>
        )}

        {report && (
          <div className="space-y-2">
            <p className={cn('text-sm font-medium', TONE_CLASS[reportTone(report)])}>
              {t(reportLabel(report).key, reportLabel(report).params)}
            </p>
            {report.errors.length > 0 && <IssuesTable issues={report.errors} />}
          </div>
        )}

      </div>
    </SidePanel>
  )
}

export default UserImportModal
